import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  X, PhoneCall, CalendarClock, User, Mail, CheckCircle2, Loader2, ShieldCheck,
  Sun, ChevronLeft, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ----------------------------------------------------------------------------
// Office hours: Mon–Sat, 10:00 AM – 7:00 PM IST, 30-min slots.
// Sundays are disabled; today's already-past slots are disabled.
// ----------------------------------------------------------------------------
const OFFICE_START_HR = 10;
const OFFICE_END_HR   = 19;   // last slot starts at 18:30 → 7:00 PM close
const SLOT_MINUTES    = 30;
const LOOKAHEAD_DAYS  = 14;

const buildSlotsForDay = (date) => {
  const out = [];
  for (let h = OFFICE_START_HR; h < OFFICE_END_HR; h++) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      const d = new Date(date);
      d.setHours(h, m, 0, 0);
      out.push(d);
    }
  }
  return out;
};

const startOfDay = (d) => {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const fmtDate = (d) =>
  d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const fmtTime = (d) =>
  d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

// ----------------------------------------------------------------------------
// Mini calendar grid (28-day rolling window). Sundays are disabled + greyed.
// ----------------------------------------------------------------------------
const MiniCalendar = ({ selected, onSelect }) => {
  const today = startOfDay(new Date());
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startWeekday = first.getDay(); // 0 = Sun
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    return cells;
  }, [month]);

  const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + LOOKAHEAD_DAYS);
  const canPrev = month > new Date(today.getFullYear(), today.getMonth(), 1);
  const canNext = month < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const monthLabel = month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="advisor-calendar">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => canPrev && setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          disabled={!canPrev}
          className="w-7 h-7 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          aria-label="Previous month"
          data-testid="advisor-cal-prev"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-sm font-black text-slate-800">{monthLabel}</div>
        <button
          type="button"
          onClick={() => canNext && setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          disabled={!canNext}
          className="w-7 h-7 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          aria-label="Next month"
          data-testid="advisor-cal-next"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 text-[10px] font-black uppercase tracking-wide text-slate-400 mb-1">
        {['S','M','T','W','T','F','S'].map((d, i) => (
          <div key={`h-${i}`} className={`text-center ${i === 0 ? 'text-rose-400' : ''}`}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const pastOrTooFar = d < today || d > maxDate;
          const isSunday = d.getDay() === 0;
          const disabled = pastOrTooFar || isSunday;
          const isSel = selected && isSameDay(d, selected);
          const isToday = isSameDay(d, today);
          return (
            <button
              key={`d-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(d)}
              className={[
                'h-8 rounded-lg text-xs font-bold transition-all',
                disabled
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-700 hover:bg-emerald-50 hover:text-emerald-900',
                isSel && 'bg-emerald-600 text-white hover:!bg-emerald-700 hover:!text-white shadow',
                !isSel && isToday && 'ring-1 ring-emerald-400',
                isSunday && !isSel && 'text-rose-300',
              ].filter(Boolean).join(' ')}
              data-testid={isSel ? 'advisor-cal-selected' : `advisor-cal-day-${d.getDate()}`}
              title={isSunday ? 'Closed on Sunday' : ''}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-slate-400 flex items-center gap-1.5">
        <Sun size={10} className="text-amber-400" /> Office hours: Mon–Sat · 10:00 AM – 7:00 PM IST
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Time-slot grid for the picked date. Filters out slots that are already
// in the past (for today) and slots within the next 30 minutes.
// ----------------------------------------------------------------------------
const TimeSlots = ({ date, value, onChange }) => {
  const now = new Date();
  const earliest = new Date(now.getTime() + 30 * 60 * 1000); // 30-min notice
  const slots = useMemo(() => (date ? buildSlotsForDay(date) : []), [date]);

  if (!date) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500 bg-slate-50">
        Pick a date on the left first.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="advisor-timeslots">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
        Available slots · {fmtDate(date)}
      </div>
      <div className="grid grid-cols-3 gap-1.5 max-h-52 overflow-y-auto pr-1">
        {slots.map((s, i) => {
          const disabled = s < earliest;
          const active = value && isSameDay(value, s) && value.getHours() === s.getHours() && value.getMinutes() === s.getMinutes();
          return (
            <button
              key={`t-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => onChange(s)}
              className={[
                'text-[11px] font-bold rounded-lg px-2 py-1.5 border transition-all',
                disabled
                  ? 'border-slate-100 text-slate-300 bg-slate-50 cursor-not-allowed'
                  : active
                  ? 'border-emerald-600 bg-emerald-600 text-white shadow'
                  : 'border-slate-200 text-slate-700 hover:border-emerald-400 hover:text-emerald-800',
              ].join(' ')}
              data-testid={active ? 'advisor-slot-active' : `advisor-slot-${s.getHours()}-${s.getMinutes()}`}
            >
              {fmtTime(s)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const AdvisorCallModal = ({ onClose }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [budget, setBudget] = useState('20');
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !slot || busy) return;
    setBusy(true);
    const scheduledAt = slot.toISOString();
    const slotLabel = `${fmtDate(slot)} · ${fmtTime(slot)}`;
    try {
      await axios.post(`${BACKEND_URL}/api/invest/waitlist`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        amount_range: budget,
        note: `Advisor call requested at ${slotLabel}${note ? ' | ' + note.trim() : ''}`,
        corridor: 'advisor-call',
      });
      setOk(true);
    } catch (_) {
      try {
        const prev = JSON.parse(localStorage.getItem('am_advisor_calls') || '[]');
        prev.push({ name, phone, email, budget, scheduledAt, note, ts: Date.now(), offline: true });
        localStorage.setItem('am_advisor_calls', JSON.stringify(prev));
      } catch { /* ignore */ }
      setOk(true);
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[9999] grid place-items-center p-4 bg-slate-900/70 backdrop-blur-sm" data-testid="advisor-call-modal">
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-emerald-400/40 max-h-[92vh] flex flex-col">
        <div className="bg-gradient-to-r from-emerald-700 via-emerald-800 to-slate-900 text-white p-5 flex items-start gap-3 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/25 grid place-items-center shrink-0">
            <PhoneCall size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 border border-emerald-300/50 text-emerald-100 rounded-full px-2 py-0.5">
              Talk to an investment advisor
            </div>
            <h3 className="text-lg sm:text-xl font-black mt-1">Schedule a free 15-min call</h3>
            <div className="text-[11px] text-white/80 mt-0.5">Pick a date and slot within office hours — we&rsquo;ll call you exactly on time.</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1 -mr-1" aria-label="Close" data-testid="advisor-close">
            <X size={20} />
          </button>
        </div>

        <div className="bg-white p-5 overflow-y-auto">
          {ok ? (
            <div className="text-center py-6" data-testid="advisor-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto"><CheckCircle2 size={30} className="text-emerald-600" /></div>
              <h4 className="text-lg font-black text-slate-900 mt-3">Call scheduled</h4>
              <p className="text-sm text-slate-600 mt-1.5">
                We&rsquo;ll call you on <strong>{fmtDate(slot)} at {fmtTime(slot)}</strong>. A confirmation WhatsApp is on its way.
              </p>
              <button type="button" onClick={onClose} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2.5 rounded-full" data-testid="advisor-done">Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4" data-testid="advisor-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Full name *</label>
                  <div className="relative mt-1">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your full name" className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm" data-testid="advisor-name" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Mobile *</label>
                  <div className="relative mt-1">
                    <PhoneCall size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} required inputMode="tel" placeholder="10-digit mobile" className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm" data-testid="advisor-phone" />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Email (optional)</label>
                  <div className="relative mt-1">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm" data-testid="advisor-email" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Interested budget</label>
                  <select value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="advisor-budget">
                    <option value="10">Up to ₹10 L</option>
                    <option value="20">₹10 – ₹20 L</option>
                    <option value="50">₹20 – ₹50 L</option>
                    <option value="100">₹50 L – ₹1 Cr</option>
                    <option value="500">₹1 Cr+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
                  <CalendarClock size={12} /> Pick date &amp; time (IST)
                </label>
                <div className="mt-2 grid md:grid-cols-2 gap-3">
                  <MiniCalendar selected={date} onSelect={(d) => { setDate(d); setSlot(null); }} />
                  <TimeSlots date={date} value={slot} onChange={setSlot} />
                </div>
                {slot && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5" data-testid="advisor-slot-label">
                    <CalendarClock size={12} /> {fmtDate(slot)} · {fmtTime(slot)}
                  </div>
                )}
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Anything specific? (optional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Preferred corridor, plot use, timeline…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="advisor-note" />
              </div>

              <button
                type="submit"
                disabled={busy || !slot}
                className="mt-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm px-5 py-3 rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="advisor-submit"
              >
                {busy
                  ? <><Loader2 size={16} className="animate-spin" /> Scheduling…</>
                  : slot
                  ? <>Schedule my call on {fmtDate(slot)} · {fmtTime(slot)}</>
                  : <>Pick a date &amp; time to continue</>}
              </button>

              <div className="flex items-start gap-2 text-[10px] text-slate-500 mt-1">
                <ShieldCheck size={12} className="text-emerald-600 shrink-0 mt-0.5" />
                <span>Free consultation. We never share your number with third parties.</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AdvisorCallModal;
